import {
  InfiniteData,
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
} from '@tanstack/react-query'
import { sortBy } from 'lodash'
import { BudgetRequestListModel } from 'src/models/budgetRequest.models'
import {
  BudgetRequestDetailResponse,
  BudgetRequestMutationResponse,
  BudgetRequestsListParams,
  BudgetRequestsListResponse,
  PatchBudgetRequestBody,
  PostBudgetRequestBody,
} from 'src/queries/budgetRequests/budgetRequests.models'
import {
  getBudgetRequestDetail,
  getBudgetRequestsList,
  patchBudgetRequest,
  postBudgetRequest,
} from 'src/queries/budgetRequests/budgetRequests.network'
import { QueryError } from 'src/queries/network.utils'
import { queryCacheOptions } from 'src/queries/queries.constants'

export const normalizeBudgetRequestsListParams = (
  params: BudgetRequestsListParams,
): BudgetRequestsListParams => {
  const normalizedParams: BudgetRequestsListParams = {}
  if (params.approver !== undefined) normalizedParams.approver = params.approver
  if (params.requester !== undefined)
    normalizedParams.requester = params.requester
  if (params.all !== undefined) normalizedParams.all = params.all
  if (params.search) normalizedParams.search = params.search
  if (params.count !== undefined) normalizedParams.count = params.count
  if (params.page !== undefined) normalizedParams.page = params.page
  if (params.sortField) normalizedParams.sortField = params.sortField
  if (params.sortDirection)
    normalizedParams.sortDirection = params.sortDirection
  if (params.statuses !== undefined) normalizedParams.statuses = params.statuses
  return normalizedParams
}

export const budgetRequestsQueryKeys = {
  all: () => ['budgetrequests'] as const,
  list: (params: BudgetRequestsListParams) =>
    [
      ...budgetRequestsQueryKeys.all(),
      'list',
      normalizeBudgetRequestsListParams(params),
    ] as const,
  detail: (budgetRequestId: string | undefined) =>
    [...budgetRequestsQueryKeys.all(), budgetRequestId] as const,
}

export const selectBudgetRequestListModels = (
  data: InfiniteData<BudgetRequestsListResponse, unknown>,
): BudgetRequestListModel[] => {
  const seen = new Set<string>()
  const budgetRequests = data.pages.flatMap((page) => page.budgetRequest)
  return sortBy(
    budgetRequests.flatMap((budgetRequest) => {
      if (seen.has(budgetRequest.id)) return []
      seen.add(budgetRequest.id)
      return [new BudgetRequestListModel(budgetRequest)]
    }),
    'updatedAt',
  ).reverse()
}

export const getBudgetRequestsLastPagination = (
  data: InfiniteData<BudgetRequestsListResponse, unknown> | undefined,
) => data?.pages[data.pages.length - 1]?.pagination

export const getBudgetRequestsTotalItems = (
  data: InfiniteData<BudgetRequestsListResponse, unknown> | undefined,
): number => data?.pages[0]?.pagination.totalItems ?? 0

export const budgetRequestsQueries = {
  list: (params: BudgetRequestsListParams) => {
    const normalizedParams = normalizeBudgetRequestsListParams(params)

    return infiniteQueryOptions({
      queryKey: budgetRequestsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) =>
        getBudgetRequestsList({
          ...normalizedParams,
          page: pageParam,
        }),
      initialPageParam: normalizedParams.page ?? 0,
      getNextPageParam: (lastPage) =>
        lastPage.pagination.numberOfPages === 0 ||
        lastPage.pagination.page >= lastPage.pagination.numberOfPages - 1
          ? undefined
          : lastPage.pagination.page + 1,
      // queryCacheOptions() (15-min stale/gc) on purpose: the Redux thunk this
      // replaces persisted the list across mounts, and 15-min freshness means a
      // remount inside the window renders cached pages without refetching.
      // Known accepted edge: if the data goes stale while loaded (>15 min on
      // page) and the user navigates away and back before gc, React Query
      // refetches every cached page sequentially on return. gcTime: 0 (the
      // useExtendInfiniteQuery convention) would avoid that replay but lose the
      // redux-persistence parity and refetch page 0 on every remount instead.
      ...queryCacheOptions(),
    })
  },
  detail: (budgetRequestId: string | undefined) =>
    queryOptions({
      queryKey: budgetRequestsQueryKeys.detail(budgetRequestId),
      queryFn: () => getBudgetRequestDetail(budgetRequestId!),
      enabled: Boolean(budgetRequestId),
      ...queryCacheOptions(),
    }),
}

export type PostBudgetRequestParams = {
  body: PostBudgetRequestBody
}

export type PatchBudgetRequestParams = {
  budgetRequestId: string
  body: PatchBudgetRequestBody
}

export const budgetRequestsMutations = {
  post: () =>
    mutationOptions<
      BudgetRequestMutationResponse,
      QueryError,
      PostBudgetRequestParams
    >({
      mutationFn: (params) => postBudgetRequest(params.body),
    }),
  patch: () =>
    mutationOptions<
      BudgetRequestMutationResponse,
      QueryError,
      PatchBudgetRequestParams
    >({
      mutationFn: (params) =>
        patchBudgetRequest(params.budgetRequestId, params.body),
    }),
}

export const budgetRequestFromDetail = (
  data: BudgetRequestDetailResponse | undefined,
): BudgetRequestListModel | undefined =>
  data !== undefined
    ? new BudgetRequestListModel(data.budgetRequest)
    : undefined
